import { useLayoutEffect, useRef } from 'react';

export const MASKED_UAC_NAME = '••••••••';
export const isProtectedUacName = (name: string) =>
  name === 'uacFirstName' || name === 'uacLastName';
const mask = (value: string) => '•'.repeat(Array.from(value).length);

/** DOM values contain bullets. Password inputs alone can reveal the last
 * character on Android. Cancelable edits are intercepted; synchronous input
 * handling masks IME/autofill changes before painting. Display privacy does
 * not protect against a hostile keyboard or replace encryption. */
export function MaskedNameInput({ id, value, disabled, required, error, onChange }: {
  id: string; value: string; disabled: boolean; required: boolean; error?: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const latest = useRef({ value, onChange, disabled });
  useLayoutEffect(() => { latest.current = { value, onChange, disabled }; }, [value, onChange, disabled]);
  useLayoutEffect(() => {
    const input = inputRef.current!;
    let pending: { start: number; end: number; type: string } | null = null;
    function edit(start: number, end: number, inserted: string) {
      if (latest.current.disabled) {
        input.value = mask(latest.current.value);
        return;
      }
      const chars = Array.from(latest.current.value);
      const incoming = Array.from(inserted.normalize('NFKC').replace(/[\r\n\t]/gu, ' '));
      const room = Math.max(0, 100 - (chars.length - (end - start)));
      const next = [...chars.slice(0, start), ...incoming.slice(0, room), ...chars.slice(end)].join('');
      latest.current.value = next;
      input.value = mask(next);
      const caret = Math.min(start + Math.min(incoming.length, room), input.value.length);
      input.setSelectionRange(caret, caret);
      latest.current.onChange(next);
    }
    function beforeInput(event: InputEvent) {
      let start = input.selectionStart ?? input.value.length;
      let end = input.selectionEnd ?? start;
      pending = { start, end, type: event.inputType };
      if (!event.cancelable) return;
      if (event.inputType.startsWith('delete')) {
        event.preventDefault();
        if (start === end) {
          if (event.inputType.includes('Backward')) start = Math.max(0, start - 1);
          else end = Math.min(input.value.length, end + 1);
        }
        edit(start, end, '');
        pending = null;
      } else if (event.inputType.startsWith('insert') && event.data != null) {
        event.preventDefault();
        edit(start, end, event.data);
        pending = null;
      } else if (event.inputType.startsWith('history')) {
        event.preventDefault();
        pending = null;
      }
    }
    function inputFallback() {
      const before = mask(latest.current.value);
      const incoming = input.value;
      input.value = before;
      const prior = pending;
      pending = null;
      if (prior?.type.startsWith('delete') && before !== incoming) {
        let { start, end } = prior;
        if (start === end) {
          const count = Math.max(1, before.length - incoming.length);
          if (prior.type.includes('Backward')) start = Math.max(0, start - count);
          else end = Math.min(before.length, end + count);
        }
        edit(start, end, '');
        return;
      }
      let start = 0;
      while (start < before.length && start < incoming.length && before[start] === incoming[start]) start++;
      let end = before.length;
      let incomingEnd = incoming.length;
      while (end > start && incomingEnd > start && before[end - 1] === incoming[incomingEnd - 1]) {
        end--; incomingEnd--;
      }
      if (before !== incoming) edit(start, end, incoming.slice(start, incomingEnd));
    }
    function paste(event: ClipboardEvent) {
      event.preventDefault();
      edit(input.selectionStart ?? 0, input.selectionEnd ?? 0, event.clipboardData?.getData('text/plain') ?? '');
    }
    function prevent(event: Event) { event.preventDefault(); }
    input.addEventListener('beforeinput', beforeInput);
    input.addEventListener('input', inputFallback);
    input.addEventListener('paste', paste);
    input.addEventListener('copy', prevent);
    input.addEventListener('cut', prevent);
    input.addEventListener('drop', prevent);
    return () => {
      input.removeEventListener('beforeinput', beforeInput);
      input.removeEventListener('input', inputFallback);
      input.removeEventListener('paste', paste);
      input.removeEventListener('copy', prevent);
      input.removeEventListener('cut', prevent);
      input.removeEventListener('drop', prevent);
    };
  }, []);
  return <div>
    <input ref={inputRef} id={id} className="input" type="password"
      value={mask(value)} onChange={() => { /* Native input listener owns edits. */ }}
      disabled={disabled} autoComplete="off" autoCorrect="off" autoCapitalize="off"
      spellCheck={false} data-lpignore="true" data-1p-ignore="true"
      aria-required={required} aria-invalid={Boolean(error)}
      aria-describedby={`${id}-privacy${error ? ` ${id}-error` : ''}`} />
    <p id={`${id}-privacy`} className="mt-1 text-xs text-neutral-700">
      Hidden as you type. Select all to replace a name. Names stay hidden on review.
    </p>
  </div>;
}
